import React, { Component } from 'react'
import { GOOGLE_MAPS_API_KEY } from '../config/googleMaps'

// Health-check page for the Google address autocomplete used on the domain
// registration form. Public and unauthenticated so the checker can reach it.
//
// This exists as a page rather than a server route because the key is
// referrer-restricted: Google only honours it for requests originating from
// hosting.breezelabs.co.uk in a browser. A plain server-side HTTP call cannot
// reproduce that - the legacy Places web service refuses referrer-restricted
// keys outright, and the Maps JS bootstrap returns HTTP 200 with an identical
// body even for a completely invalid key. Running the query in a real browser
// on the real origin is the only way to get a truthful answer.
//
// server/nodeProcesses/googleAutocompleteCheck.js renders this hourly with
// headless Chrome and greps the resulting DOM for the status token below.

const SCRIPT_ID = 'gapi-address-check-maps'
const QUERY = '10 downing street'
// Chrome is given a longer budget than this, so a hang surfaces as our own
// FAIL_TIMEOUT rather than as an inconclusive PENDING.
const TIMEOUT_MS = 15000

class GapiAddressCheck extends Component {
    state = { status: 'GAPI_CHECK_PENDING', detail: '' }
    settled = false

    componentDidMount() {
        // Google calls this global when it rejects the key - wrong/expired key,
        // referrer not allowed, billing disabled, API not enabled. It is the
        // only dependable auth signal, since the script tag loads fine anyway.
        window.gm_authFailure = () => this.settle('GAPI_CHECK_FAIL_AUTH')

        this.timer = setTimeout(() => this.settle('GAPI_CHECK_FAIL_TIMEOUT'), TIMEOUT_MS)

        this.loadMaps()
            .then(() => this.runQuery())
            .catch(() => this.settle('GAPI_CHECK_FAIL_SCRIPT'))
    }

    componentWillUnmount() {
        clearTimeout(this.timer)
    }

    // First result wins: gm_authFailure and the query callback can both fire.
    settle(status, detail) {
        if (this.settled) {
            return
        }
        this.settled = true
        clearTimeout(this.timer)
        this.setState({ status: status, detail: detail || '' })
    }

    loadMaps() {
        return new Promise((resolve, reject) => {
            if (window.google && window.google.maps && window.google.maps.places) {
                return resolve()
            }
            let script = document.getElementById(SCRIPT_ID)
            if (!script) {
                script = document.createElement('script')
                script.id = SCRIPT_ID
                script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`
                script.async = true
                document.head.appendChild(script)
            }
            script.addEventListener('load', resolve)
            script.addEventListener('error', reject)
        })
    }

    runQuery() {
        if (!window.google || !window.google.maps || !window.google.maps.places) {
            return this.settle('GAPI_CHECK_FAIL_SCRIPT')
        }
        const service = new window.google.maps.places.AutocompleteService()
        service.getPlacePredictions({ input: QUERY, types: ['geocode'] }, (predictions, status) => {
            if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
                return this.settle('GAPI_CHECK_FAIL_STATUS', status)
            }
            if (!predictions || predictions.length === 0) {
                return this.settle('GAPI_CHECK_FAIL_NO_RESULTS')
            }
            this.settle('GAPI_CHECK_OK', `${predictions.length} predictions for "${QUERY}"`)
        })
    }

    render() {
        return (
            <div style={{ margin: '40px', color: 'white' }}>
                <h3>Google address autocomplete check</h3>
                {/* googleAutocompleteCheck.js greps the rendered DOM for this
                    token - keep the id and the GAPI_CHECK_* values stable. */}
                <div id="gapi-status">{this.state.status}</div>
                <div id="gapi-detail">{this.state.detail}</div>
            </div>
        )
    }
}

export default GapiAddressCheck
